import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import api from '../api/api';

export const TID_SCRIPT = 'https://sso-forms-prod.t-static.ru/tid/widget.js';
const MOBILE_KEY = 'prokatTidAttempt';
let scriptPromise;
let startQueue = Promise.resolve();
const bankButtonSx = {
  py: 1.35, px: 2.5, minHeight: 52, borderRadius: 2, textTransform: 'none',
  fontWeight: 700, color: '#0b1634', borderColor: '#dcd6cc', justifyContent: 'flex-start',
};

function BankButton({ disabled, onClick }) {
  return <Button fullWidth variant="outlined" size="large" disabled={disabled} onClick={onClick}
    startIcon={<Box component="span" aria-hidden="true" sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid',
      placeItems: 'center', bgcolor: '#ffdd2d', color: '#111', fontWeight: 900 }}>Т</Box>}
    sx={bankButtonSx}>Войти с T-ID</Button>;
}
export function loadTidWidget() {
  if (window.TidSDK) return Promise.resolve(window.TidSDK);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const fail = () => { clearTimeout(timer); script.remove(); scriptPromise = null; reject(new Error('widget_unavailable')); };
    const timer = setTimeout(fail, 15000);
    script.src = TID_SCRIPT; script.async = true;
    script.onload = () => { clearTimeout(timer); if (window.TidSDK) resolve(window.TidSDK); else fail(); };
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function readTidAttempt() {
  try {
    const value = JSON.parse(localStorage.getItem(MOBILE_KEY));
    if (/^[A-Za-z0-9_-]{43}$/.test(value?.attempt_secret) && value.expiresAt > Date.now()
        && value.expiresAt <= Date.now() + 600000) return value;
  } catch (_) { /* Ignore a stale attempt, never infer successful login. */ }
  localStorage.removeItem(MOBILE_KEY);
  return null;
}

export function restoreTidCart() {
  const attempt = readTidAttempt();
  if (!attempt?.cart || sessionStorage.getItem('storefrontCart') !== null) return;
  try {
    if (attempt.cart.length <= 65536 && Array.isArray(JSON.parse(attempt.cart))) sessionStorage.setItem('storefrontCart', attempt.cart);
  } catch (_) { /* Invalid saved input never authorizes or submits an order. */ }
}

export default function TidLogin(props) {
  // Keep the unfinished integration out of both public web and Android builds.
  if (process.env.REACT_APP_ENABLE_TID_LOGIN !== 'true') return null;
  return <EnabledTidLogin {...props} />;
}

function EnabledTidLogin({ acceptance, ready, disabled, returnToCart, onComplete, onPending = () => {}, beforeWebLogin = () => {} }) {
  const mobile = process.env.REACT_APP_CLIENT_APP === 'true';
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [configRetry, setConfigRetry] = useState(0);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [busy, setBusy] = useState(false);
  const [widgetVisible, setWidgetVisible] = useState(false);
  const [attempt, setAttempt] = useState(() => mobile ? readTidAttempt() : null);
  const container = useRef(null);
  const callbacks = useRef({ onComplete, onPending, beforeWebLogin });
  callbacks.current = { onComplete, onPending, beforeWebLogin };
  const payload = JSON.stringify({ ...acceptance, return_to_cart: returnToCart, mobile });
  const reset = () => { localStorage.removeItem(MOBILE_KEY); setAttempt(null); };
  useEffect(() => {
    let active = true;
    setConfigError(false);
    Promise.resolve().then(() => api.get('/client-portal/tid/config', { skipLoading: true, timeout: 15000 })).then(({ data }) => {
      if (typeof data?.enabled !== 'boolean') throw new Error('Invalid T-ID configuration');
      if (active) setConfig(data);
    }).catch(() => { if (active) setConfigError(true); });
    return () => { active = false; };
  }, [configRetry]);
  useEffect(() => {
    if (!configError) return undefined;
    const retryConfig = () => setConfigRetry(value => value + 1);
    window.addEventListener('online', retryConfig);
    return () => window.removeEventListener('online', retryConfig);
  }, [configError]);
  useEffect(() => {
    callbacks.current.onPending(Boolean(attempt || busy));
    return () => callbacks.current.onPending(false);
  }, [attempt, busy]);
  useEffect(() => {
    setWidgetVisible(false);
    if (mobile || !config?.enabled || !ready || disabled) return undefined;
    let active = true, timer, renderTimer, observer;
    const element = container.current;
    // The official SDK initializes each HTMLElement once (WeakSet). Use a fresh
    // child for each attempt so retry/changed consent cannot leave an empty slot.
    const mount = document.createElement('div');
    element.replaceChildren(mount);
    setError('');
    // Serialize starts so a late response cannot replace the cookie belonging
    // to the visible widget (including React StrictMode's double effect).
    startQueue = startQueue.catch(() => {}).then(async () => {
      const SDK = await loadTidWidget();
      if (!active) return null;
      return [SDK, await api.post('/client-portal/tid/start', JSON.parse(payload), { skipLoading: true })];
    });
    startQueue
      .then(result => {
        if (!active || !result) return;
        const [SDK, { data }] = result;
        callbacks.current.beforeWebLogin();
        const sdk = new SDK(data.auth_params);
        const checkButton = () => {
          if (active && mount.querySelector('button, a[href], [role="button"]')) {
            setWidgetVisible(true); setError(''); clearTimeout(renderTimer); observer.disconnect();
          }
        };
        observer = new MutationObserver(checkButton);
        observer.observe(mount, { childList: true, subtree: true });
        renderTimer = setTimeout(() => {
          if (active) setError('Не удалось загрузить кнопку T-ID. Повторите попытку.');
        }, 15000);
        sdk.addButton({ container: mount, size: 'm', color: 'grey', text: 'Войти с T-ID',
          textPosition: 'right', target: '_self', recognized: false });
        checkButton();
        timer = setTimeout(() => { if (active) setRetry(value => value + 1); }, 9 * 60000);
      }).catch(() => { if (active) setError('Не удалось загрузить вход T-ID. Повторите попытку.'); });
    return () => { active = false; clearTimeout(timer); clearTimeout(renderTimer); observer?.disconnect(); mount.remove(); };
  }, [mobile, config?.enabled, ready, disabled, payload, retry]);
  useEffect(() => {
    if (!mobile || !attempt) return undefined;
    let active = true, inFlight = false;
    const poll = async () => {
      if (!active || inFlight || document.visibilityState === 'hidden' || window.prokatProstAppPaused) return;
      if (Date.now() >= attempt.expiresAt) { reset(); setError('Время входа истекло. Попробуйте ещё раз.'); return; }
      inFlight = true;
      try {
        const { data } = await api.post('/client-portal/tid/complete', { attempt_secret: attempt.attempt_secret }, { skipLoading: true, timeout: 15000 });
        if (!active) return;
        if (data.status === 'completed' && data.token) {
          reset(); await callbacks.current.onComplete(data.token, data.return_to_cart === true);
        } else if (data.status === 'failed') { reset(); setError(data.message); }
      } catch (err) {
        if (!active) return;
        if ([400, 401, 403, 422].includes(err.response?.status)) { reset(); setError('Начните вход через T-ID заново.'); }
        else setError('Нет связи с сервером. Проверим вход после восстановления подключения.');
      } finally { inFlight = false; }
    };
    poll(); const timer = setInterval(poll, 2500);
    const nativeResult = event => { if (event.detail?.error) setError(event.detail.error); poll(); };
    window.addEventListener('prokat-tid-result', nativeResult);
    document.addEventListener('visibilitychange', poll); window.addEventListener('online', poll);
    return () => { active = false; clearInterval(timer); window.removeEventListener('prokat-tid-result', nativeResult);
      document.removeEventListener('visibilitychange', poll); window.removeEventListener('online', poll); };
  }, [mobile, attempt]);
  const cancel = () => {
    if (attempt) api.post('/client-portal/tid/cancel', { attempt_secret: attempt.attempt_secret }, { skipLoading: true }).catch(() => {});
    window.ProkatTid?.postMessage(JSON.stringify({ action: 'cancel' }));
    reset(); setError('');
  };
  const startMobile = async () => {
    if (!window.ProkatTid?.postMessage) { setError('Установите новую версию приложения для входа через T-ID.'); return; }
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/client-portal/tid/start', JSON.parse(payload), { timeout: 15000 });
      if (!/^[A-Za-z0-9_-]{43}$/.test(data.attempt_secret) || data.expires_in !== 600
          || data.redirect_uri !== 'prokatprost-app://tid-return') throw new Error('Invalid SDK configuration');
      const cart = sessionStorage.getItem('storefrontCart');
      const saved = { ...data, expiresAt: Date.now() + data.expires_in * 1000,
        ...(cart && cart.length <= 65536 ? { cart } : {}) };
      localStorage.setItem(MOBILE_KEY, JSON.stringify(saved)); setAttempt(saved);
      window.ProkatTid.postMessage(JSON.stringify({ action: 'start', ...data }));
    } catch (err) { setError(err.response?.data?.detail || 'Не удалось начать вход T-ID. Попробуйте снова.'); }
    finally { setBusy(false); }
  };
  if (configError) return <Box>
    <BankButton disabled />
    <Alert severity="warning" sx={{ mt: 1 }} action={<Button onClick={() => setConfigRetry(value => value + 1)}>Повторить</Button>}>
      Не удалось проверить доступность входа T-ID. Проверьте соединение и повторите попытку.
    </Alert>
  </Box>;
  if (config && (!config.enabled || mobile && !config.mobile_enabled)) return null;
  const showWidget = widgetVisible && ready && !disabled;
  return <Box sx={{ width: '100%' }}>
    {mobile ? <BankButton disabled={!config?.enabled || !ready || disabled || busy || Boolean(attempt)} onClick={startMobile} />
      : <>{!showWidget && <BankButton disabled />}
        <Box ref={container} data-testid="tid-widget" sx={{ width: '100%', display: showWidget ? 'block' : 'none',
          '& button': { ...bankButtonSx, width: '100%', border: '1px solid #dcd6cc', backgroundColor: '#fff',
            fontFamily: 'inherit', fontSize: '0.9375rem', lineHeight: 1.75 },
          '& button:hover': { backgroundColor: '#faf8f5' },
          '& button span': { fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' },
        }} />
        {!ready && <Typography variant="body2" color="text.secondary">Для входа с T-ID примите условия выше.</Typography>}</>}
    {attempt && <Alert severity="info" sx={{ mt: 1 }} action={<Button onClick={cancel}>Отменить</Button>}>
      Подтвердите вход в Т-Банке и вернитесь в приложение.</Alert>}
    {error && <Alert severity="warning" sx={{ mt: 1 }} action={!attempt && <Button onClick={() => setRetry(value => value + 1)}>Повторить</Button>}>{error}</Alert>}
  </Box>;
}
